import { useMutation } from "@apollo/client/react";
import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { css } from "@linaria/core";
import { Button, Form, Input, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { assert } from "ts-essentials";
import {
  CreateIngredientDocument,
  type CreateIngredientInput,
  type GenericIngredientFragment,
  type IngredientFragment,
} from "../../gql/generated/graphqlSchema.tsx";
import { footerRow } from "../../styles/splitPaneSearch.styles.ts";

const formStyle = css`
  padding: var(--ant-padding-lg);
`;

type FormValues = {
  name: string;
};

export const CreateManualView = ({
  genericIngredient = undefined,
  initialName,
  onBack = undefined,
  onCreated,
}: {
  readonly genericIngredient?: GenericIngredientFragment;
  readonly initialName: string;
  readonly onBack?: () => void;
  readonly onCreated: (ingredient: IngredientFragment) => void;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [createIngredient] = useMutation(CreateIngredientDocument);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    if (submitting) return;
    setSubmitting(true);
    form
      .validateFields()
      .then(async (values) => {
        const input: CreateIngredientInput = {
          name: values.name,
          genericIngredientId: genericIngredient?.id,
        };
        const { data } = await createIngredient({ variables: { input } });
        const created = data?.createIngredient;
        assert(created != null, "createIngredient returned no data");
        onCreated(created);
      })
      .catch(() => {})
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Form
        className={formStyle}
        form={form}
        initialValues={{ name: initialName }}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          label={t("ingredients.name")}
          name="name"
          rules={[{ required: true, message: t("ingredients.validation.nameRequired") }]}
        >
          <Input autoFocus placeholder={t("ingredients.namePlaceholder")} />
        </Form.Item>
      </Form>
      <div className={footerRow}>
        <Space wrap>
          {onBack != null ? (
            <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
              {t("common.back")}
            </Button>
          ) : null}
          <Button color="primary" icon={<PlusOutlined />} loading={submitting} onClick={handleSubmit} variant="solid">
            {t("common.create")}
          </Button>
        </Space>
      </div>
    </>
  );
};
